import { Button, Space, Spacing } from "@p0security/ui";
import { RangeValueType } from "rc-picker/lib/PickerInput/RangePicker";

import { FilterOption, TimeRange } from "../types";
import { RequestDateFilters } from "./RequestHistoryDateFilters";
import { SearchBar } from "./RequestHistorySearchBar";
import { DropdownFilter } from "./filters/DropdownFilter";

/**
 * A component that renders the filters for the active requests and pre-approval pages.
 *
 * @param filters - The filters for the active requests and pre-approval pages
 * @param options - The options for the filters
 * @param hideApprovalDateFilter - Whether to hide the approval date filter
 * @param hideExport - Whether to hide the export button
 * @param resetFilters - The function to call when the reset filters button is clicked
 */
export const RequestFilters: React.FC<{
  filters: {
    searchQuery: string | null;
    setSearchQuery: (query: string | null) => void;
    requestedFilter: TimeRange;
    setRequestedFilter: (
      value: RangeValueType<Date> | null | undefined
    ) => void;
    approvedFilter: TimeRange;
    setApprovedFilter: (value: RangeValueType<Date> | null | undefined) => void;
    requestorFilter: string | null;
    setRequestorFilter: (value: string | null) => void;
    approverFilter: string | null;
    setApproverFilter: (value: string | null) => void;
    resourceFilter: string | null;
    setResourceFilter: (value: string | null) => void;
    parentFilter: string | null;
    setParentFilter: (value: string | null) => void;
  };
  options: {
    requestorOptions: FilterOption[];
    approverOptions: FilterOption[];
    resourceOptions: FilterOption[];
    parentOptions: FilterOption[];
  };
  hideApprovalDateFilter?: boolean;
  hideExport?: boolean;
  resetFilters?: () => void;
}> = ({
  filters,
  options,
  hideApprovalDateFilter = false,
  hideExport = false,
  resetFilters,
}) => {
  const {
    searchQuery,
    setSearchQuery,
    requestedFilter,
    setRequestedFilter,
    approvedFilter,
    setApprovedFilter,
    requestorFilter,
    setRequestorFilter,
    approverFilter,
    setApproverFilter,
    resourceFilter,
    setResourceFilter,
    parentFilter,
    setParentFilter,
  } = filters;

  const { requestorOptions, approverOptions, resourceOptions, parentOptions } =
    options;

  return (
    <Spacing asChild mb="1em">
      <Space
        direction="vertical"
        size="small"
        /* eslint-disable-line react/forbid-component-props */ style={{
          width: "100%",
        }}
      >
        <SearchBar
          onChange={setSearchQuery}
          searchQuery={searchQuery}
          hideExport={hideExport}
        />
        <RequestDateFilters
          approvedDateFilter={approvedFilter}
          requestedDateFilter={requestedFilter}
          onApprovalFilterChange={setApprovedFilter}
          onRequestFilterChange={setRequestedFilter}
          hideApprovalDateFilter={hideApprovalDateFilter}
        />
        <Space wrap>
          <DropdownFilter
            placeholder="Filter by Requestor"
            value={requestorFilter}
            options={requestorOptions}
            onChange={setRequestorFilter}
          />
          <DropdownFilter
            placeholder="Filter by Approver"
            value={approverFilter}
            options={approverOptions}
            onChange={setApproverFilter}
          />
          <DropdownFilter
            placeholder="Filter by Resource"
            value={resourceFilter}
            options={resourceOptions}
            onChange={setResourceFilter}
          />
          <DropdownFilter
            placeholder="Filter by Location"
            value={parentFilter}
            options={parentOptions}
            onChange={setParentFilter}
          />
          {resetFilters && (
            <Button onClick={resetFilters} size="small" type="link">
              Clear
            </Button>
          )}
        </Space>
      </Space>
    </Spacing>
  );
};
